import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input,
    OnInit,
    Output,
    ViewEncapsulation
} from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { Coerce, KalDate } from '@kalidea/kaligraphi';
import gql from 'graphql-tag';
import { BehaviorSubject, Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { GraphqlService } from 'src/app/_core/services/graphql.service';
import { buildProviders, ListingFiltersComponent } from 'src/app/_shared/components/listing/listing-filters.component';
import { arrayEmpty } from 'src/app/utils/array.utils';
import { rangeToFilters } from 'src/app/utils/date.utils';
import { environment } from 'src/environments/environment';
import {
    ArrayOfChampDto,
    ArrayOfChampListeValeurDto,
    ArrayOfOrganisationDto,
    ArrayOfSiteDto,
    ChampDto,
    ChampListeValeurDto,
    CriteresChampDtoInput,
    CriteresChampListeValeurDtoInput,
    FiltrageBenefModeExpertModelDtoInput,
    OrganisationDto,
    SiteDto
} from 'src/graphql/generated-types';

enum BeneficiaryListEnfantsTypeEnum {
    HaveEnfantACharge = 'HaveEnfantACharge',
    HaveEnfantRattache = 'HaveEnfantRattache',
    HaveEnfantHandicape = 'HaveEnfantHandicape'
}

// typage des données du formulaire
interface FiltersFormStructure {
    TexteRecherche: string;

    Identite: {
        NomBenef: string,
        PrenomBenef: string,
        Matricule: string,
        DateNaissance: { startDate: KalDate, endDate: KalDate },
        Sexe: number | null,
        DateCreation: { startDate: KalDate, endDate: KalDate }
    };

    Coordonnees: {
        Adresse: string,
        CodePostal: string,
        Ville: string,
        Telephone: string,
        Email: string
    };

    Criteres: {
        AfficheClotures: boolean,
        DateEmbauche: { startDate: KalDate, endDate: KalDate },
        Contrat: number[],
        Groupes: number[],
        Metier: number[],
        FiltreOrganisation: number[],
        FiltresSite: number[],
        Categorie: number[],
        IsCriteresSociauxValides: boolean | null
    };

    ChampsPersonnalises;

    AyantsDroits: {
        NomAD: string,
        PrenomAD: string,
        AgeMinAyantDroity: number,
        AgeMaxAyantDroity: number,
        IdCycleScolaire: number | null,
        Enfants: BeneficiaryListEnfantsTypeEnum[],
        IsConjointValide: boolean | null,
        IsEnfantValide: boolean | null,
        IsExterneValide: boolean | null
    };

    Synchronisation: {
        VoirBenefNonSynchro: boolean,
        VoirBenefSubNonSynchro: boolean,
        VoirBenefSansProfilKalidea: boolean,
        VoirBenefEnErreur: boolean,
    };
}

@Component({
    selector: 'app-shared-beneficiary-list-filters',
    templateUrl: './shared-beneficiary-list-filters.component.html',
    styleUrls: ['./shared-beneficiary-list-filters.component.sass'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: buildProviders(SharedBeneficiaryListFiltersComponent),
    standalone: false
})
export class SharedBeneficiaryListFiltersComponent extends ListingFiltersComponent implements OnInit {

    EnfantsTypeEnum = BeneficiaryListEnfantsTypeEnum;

    personnalFields: ChampDto[] = [];

    fieldsList: ChampListeValeurDto[] = [];

    form: FormGroup;

    AfficheClotures = false; // est ce qu'on affiche les clôturés ?

    displayFilters = false;

    fieldsTypesCodes = environment.constants.fieldsTypesCodes;

    filteredSites: SiteDto[] = [];

    filteredOrganisations: OrganisationDto[] = [];

    @Coerce('boolean')
    @Input() isModalContext = false;

    @Input() beneficiariesListLoading = new BehaviorSubject(false);

    @Output() readonly apply = new EventEmitter<FiltrageBenefModeExpertModelDtoInput>();

    private _sitesGeneriques: SiteDto[] = [];

    private _organisationsGeneriques: OrganisationDto[] = [];

    constructor(private readonly graphql: GraphqlService,
                protected readonly cdr: ChangeDetectorRef,
                private readonly fb: FormBuilder) {
        super(cdr);
    }

    get champsPersonnalisesForm(): FormArray {
        return this.form.get('ChampsPersonnalises') as FormArray;
    }

    get identityFiltersCount(): number {
        const identityFilters = this.form.value.Identite;
        return [
                'NomBenef',
                'PrenomBenef',
                'Matricule',
                'Sexe',
            ].reduce((count, key) => (!!identityFilters[key]) ? count + 1 : count, 0)
            + [
                'DateNaissance',
                'DateCreation',
            ].reduce((count, key) => (!!identityFilters[key]?.startDate || identityFilters[key]?.endDate) ? count + 1 : count, 0);
    }

    get coordinatesFiltersCount(): number {
        const value = this.form.value.Coordonnees;
        return Object.keys(value).reduce((count, key) => !!value[key] ? count + 1 : count, 0);
    }

    get ayantDroitFiltersCount(): number {
        // La valeur false est considérée comme une valeur truthy pour le décompte des filtres sur les ayants droits
        const hasValue = (value: any): boolean => (value === false) || !!value;

        const values = this.form.value.AyantsDroits;
        return Object.keys(values).reduce((count, key) => hasValue(values[key]) ? count + 1 : count, 0);
    }

    get synchronisationFiltersCount(): number {
        const value = this.form.value.Synchronisation;
        return Object.keys(value).reduce((count, key) => !!value[key] ? count + 1 : count, 0);
    }

    get personalizedFieldFiltersCount(): number {
        return this.form.value.ChampsPersonnalises.reduce((count, v, i) => {
            if (this.personnalFields[i].ChampTypeDonnee.Code === this.fieldsTypesCodes.BOO) {
                return (v === null) ? count : count + 1;
            }
            return !!v ? count + 1 : count;
        }, 0);
    }

    get criteriasFiltersCount(): number {
        const criteriasFilters = this.form.value.Criteres;
        return [
                'Contrat',
                'Groupes',
                'Metier',
                'FiltreOrganisation',
                'FiltresSite',
                'Categorie',
                'IsCriteresSociauxValides'
            ].reduce((count, key) => (!!criteriasFilters[key]) ? count + 1 : count, 0)
            + ((!!criteriasFilters.DateEmbauche?.startDate || criteriasFilters.DateEmbauche?.endDate) ? 1 : 0);
    }

    get coordinatesLabel(): string {
        const count = this.coordinatesFiltersCount;
        return count > 0 ? `Coordonnées (${count})` : 'Coordonnées';
    }

    get personalizedFieldLabel(): string {
        const count = this.personalizedFieldFiltersCount;
        return count > 0 ? `Champs personnalisés (${count})` : 'Champs personnalisés';
    }

    get identityLabel(): string {
        const count = this.identityFiltersCount;
        return count > 0 ? `Identité (${count})` : 'Identité';
    }

    get criteriasLabel(): string {
        const count = this.criteriasFiltersCount;
        return count > 0 ? `Critères (${count})` : 'Critères';
    }

    get synchronisationLabel(): string {
        const count = this.synchronisationFiltersCount;
        return count > 0 ? `Synchronisation (${count})` : 'Synchronisation';
    }

    get ayantDroitLabel(): string {
        const count = this.ayantDroitFiltersCount;
        return count > 0 ? `Ayants droit (${count})` : 'Ayants droit';
    }

    getValeursNonClotures(field: ChampDto): ChampListeValeurDto[] {
        return (field.ListChampListeValeur.ChampListeValeurDTO || []).filter(v => !v.IsCloture);
    }

    transformFilters(searchbar?: string): Record<string, any> {
        const filters = {...this.getFilters()};

        filters.TexteRecherche = searchbar;

        return filters;
    }

    cancelFilters(): void {
        this.updateForm();
    }

    searchBarValue(): string {
        return this.filters.TexteRecherche;
    }

    resetFilters(): void {
        this.form.reset({
            Criteres: {
                value: {
                    PrendreOrgaAssocie: false,
                    PrendreSiteAssocie: false
                }
            },
            Synchronisation: {
                VoirBenefNonSynchro: false,
                VoirBenefSubNonSynchro: false,
                VoirBenefSansProfilKalidea: false,
                VoirBenefEnErreur: false,
            }
        });
    }

    getFieldsList(code: string): ChampListeValeurDto[] {
        return this.fieldsList
            ?.filter(f => f.Champ.Code === code)
            ?.filter(f => (!f.IsCloture || this.AfficheClotures)) || [];
    }

    updateForm(filters = this.filters): void {
        this.form.patchValue(filters, {emitEvent: false});
    }

    /**
     * La structure des filtres est différente pour chaque champs, du coup il faut faire pleins de petits traitements manuels
     * Lorsque les champs possèdent des similitudes, on va essayer d'utiliser la destructuration
     */
    getFilters(): FiltrageBenefModeExpertModelDtoInput {
        const {
            TexteRecherche,
            Identite: {Sexe, DateNaissance, DateCreation, ...IdentiteFields},
            Coordonnees: {...CoordonneesFields},
            Criteres: {
                Contrat,
                Groupes,
                Metier,
                Categorie,
                FiltresSite,
                FiltreOrganisation,
                DateEmbauche,
                IsCriteresSociauxValides
            },
            ChampsPersonnalises,
            AyantsDroits: {Enfants, ...AyantsDroitsFields},
            Synchronisation
        } = this.form.value as FiltersFormStructure;

        const filterSelectedFields = (code: string, idsList: number[]) => {
            return this.fieldsList
                .filter(f => f.Champ.Code === code)
                .filter(f => idsList?.includes(f.Id))
                .map(f => {
                    return {Id: f.Champ.Id, Valeur: idsList?.join(',')};
                });
        };

        const sexList = filterSelectedFields('SEX', [Sexe]);
        const contractsList = filterSelectedFields('CTRAT', Contrat);
        const groupsList = filterSelectedFields('GROUP', Groupes);
        const jobsList = filterSelectedFields('METIER', Metier);
        const categoriesList = filterSelectedFields('CATEG', Categorie);

        // Ne peut pas être cumulé avec les autres filtres
        if (TexteRecherche) {
            return {
                TexteRecherche
            };
        }

        // gestion des dates
        const ListeDateRanges: {
            DateNaissanceStart?,
            DateNaissanceEnd?,
            DateNaissanceOperator?,
            DateCreationStart?,
            DateCreationEnd?,
            DateCreationOperator?,
            DateEmbaucheStart?,
            DateEmbaucheEnd?,
            DateEmbaucheOperator?,
        } = {};

        const datesControlValueList = {
            DateNaissance,
            DateCreation,
            DateEmbauche
        };

        // pour chacune des dates
        Object.keys(datesControlValueList).forEach(dateKey => {
            const date = datesControlValueList[dateKey];
            if (date) {
                // on va récupérer la range avec l'opérateur relié
                const range = rangeToFilters(datesControlValueList[dateKey]);
                if (range.operator !== 'Entre' && !range.start) {

                    range.start = range.end;
                    range.end = null;
                }

                // et on va créer les clefs correspondantes dans l'objet que l'on va déstructurer dans la liste des filtres
                ['start', 'end', 'operator'].forEach(key => {
                    const keyCamelCase = key.charAt(0).toUpperCase() + key.slice(1);
                    ListeDateRanges[dateKey + keyCamelCase] = range[key];
                });
            }
        });

        // le typage est perdu dans cette structure complexe
        const ListeFiltres: any[] = [
            ...(sexList || []),
            ...(contractsList || []),
            ...(groupsList || []),
            ...(jobsList || []),
            ...(categoriesList || []),
            // pour les champs personnalisés, on a que la valeur, il faut récupérer l'ID du champs correspondant
            ...(ChampsPersonnalises || []).map((v: any, i) => {
                const {ChampTypeDonnee: {Code}, Id} = this.personnalFields[i];
                // formatter les données avant de les envoyer
                if (v) {
                    switch (Code) {
                        case this.fieldsTypesCodes.DAT:
                            // L'API ne s'attend pas à récupérer une date au format ISO ici mais un format dd/MM/yyyy HH:mm:ss
                            v = new KalDate(v).toFormat(environment.date.format.human);
                            break;
                        case this.fieldsTypesCodes.BOO:
                            v = !!v ? '1' : '0';
                    }
                }

                return v === null ? null : ({Id, Valeur: '' + v});
            })
        ];

        // gestion des enfants
        type EnfantType = keyof typeof BeneficiaryListEnfantsTypeEnum;
        const ADEnfants: Partial<Record<EnfantType, boolean>> = {};
        if (Enfants) {
            (['HaveEnfantHandicape', 'HaveEnfantRattache', 'HaveEnfantACharge'] as EnfantType[]).forEach(key => {
                if (Enfants.includes(key as BeneficiaryListEnfantsTypeEnum)) {
                    ADEnfants[key] = true;
                }
            });
        }

        // Gestion des Ages des AD : remise à null
        ['AgeMinAyantDroit', 'AgeMaxAyantDroit'].forEach(key => {
            if (!AyantsDroitsFields[key]) {
                AyantsDroitsFields[key] = null;
            }
        });

        return {
            // identité
            ...IdentiteFields,
            // coordonnées
            ...CoordonneesFields,
            // critères
            FiltreOrganisation: (FiltreOrganisation || []).join(','),
            PrendreOrgaAssocie: false,
            FiltreSite: (FiltresSite || []).join(','),
            PrendreSiteAssocie: false,
            IsCriteresSociauxValides,

            // Ayant-droits
            ...AyantsDroitsFields,
            ...ADEnfants,

            // synchronisation
            ...Synchronisation,

            // dates
            ...ListeDateRanges,

            // general
            ListeFiltres: {
                KeyValueModelDTO: ListeFiltres.filter(f => !!f).map(f => ({Id: f.Id, Valeur: f.Valeur}))
            },
        };
    }

    private createFiltersForm(): void {
        this.form = this.fb.group({
            TexteRecherche: '',
            Identite: this.fb.group({
                NomBenef: '',
                PrenomBenef: '',
                Matricule: '',
                DateNaissance: {},
                Sexe: [],
                DateCreation: {},
            }),
            Coordonnees: this.fb.group({
                Adresse: '',
                CodePostal: '',
                Ville: '',
                Telephone: '',
                Email: ''
            }),
            Criteres: this.fb.group({
                AfficheClotures: false,
                DateEmbauche: '', // todo DateRange
                Contrat: [],
                Groupes: [],
                Metier: [],
                FiltreOrganisation: [],
                PrendreOrgaAssocie: false,
                FiltresSite: [],
                PrendreSiteAssocie: false,
                Categorie: [],
                IsCriteresSociauxValides: [],
            }),
            ChampsPersonnalises: this.fb.array([]),
            AyantsDroits: this.fb.group({
                NomAD: '',
                PrenomAD: '',
                AgeMinAyantDroit: null,
                AgeMaxAyantDroit: null,
                IdCycleScolaire: [],
                Enfants: [],
                IsConjointValide: [],
                IsEnfantValide: [],
                IsExterneValide: [],
            }),
            Synchronisation: this.fb.group({
                VoirBenefNonSynchro: false,
                VoirBenefSubNonSynchro: false,
                VoirBenefSansProfilKalidea: false,
                VoirBenefEnErreur: false,
            })
        });

        this.watch$ = this.form.get('Criteres.AfficheClotures').valueChanges.pipe(
            tap(v => {
                this.AfficheClotures = v;
                this.filteredSites = this._sitesGeneriques.filter(s => v || !s.IsCloture);
                this.filteredOrganisations = this._organisationsGeneriques.filter(o => v || !o.IsCloture);

                if (!this.AfficheClotures) {
                    this.clearArchived();
                }

                this.cdr.markForCheck();
            })
        );
    }

    private clearArchived(): void {
        [
            'Criteres.Contrat',
            'Criteres.Groupes',
            'Criteres.Metier',
            'Criteres.Categorie',
            'AyantsDroits.IdCycleScolaire',
        ].forEach((key) => {
            let value = this.form.get(key).value || [];
            value = value.filter(id => !!this.fieldsList.find(f => f.Id === id && !f.IsCloture));
            this.form.get(key).patchValue(arrayEmpty(value) ? null : value, {emitEvent: false});
        });

        const formToProp = {
            FiltreOrganisation: 'filteredOrganisations',
            FiltresSite: 'filteredSites'
        };

        Object.keys(formToProp).forEach(key => {
            let values = this.form.get('Criteres.' + key).value as number[] || [];
            values = values.filter(id => !!this[formToProp[key]].find(e => e.Id === id));
            this.form.get('Criteres.' + key).patchValue(arrayEmpty(values) ? null : values);
        });
    }

    private initList$(): Observable<{
        GetChampListeValGenerique: ArrayOfChampListeValeurDto,
        GetListeChampsGenerique: ArrayOfChampDto,
        GetListeSiteGenerique: ArrayOfSiteDto,
        GetListeOrganisationGenerique: ArrayOfOrganisationDto
    }> {
        type InitBeneficiaryListType = 'GetChampListeValGenerique'
            | 'GetListeChampsGenerique'
            | 'GetListeSiteGenerique'
            | 'GetListeOrganisationGenerique';
        return this.graphql.mutateMultiple<InitBeneficiaryListType>({
            mutation: gql`
                mutation BenefListeFiltersInitialisation(
                    $contexte: ContextInfosInput
                    $criteres: CriteresBenefDTOInput
                    $criteres2: CriteresChampListeValeurDTOInput
                    $criteres3: CriteresChampDTOInput
                ) {
                    GetInitialisationModeExpertModel(contexte: $contexte, criteres: $criteres){
                        ListeVues {
                            VueDTO {
                                Code
                                Id
                            }
                        }
                        VueParDefaut {
                            Id
                        }
                        NombreBenefTot
                    }
                    GetChampListeValGenerique(contexte: $contexte, critere: $criteres2) {
                        ChampListeValeurDTO {
                            IsCloture
                            Id
                            Valeur
                            OrdreAffichage
                            Champ {
                                Id
                                Code
                                Libelle
                            }
                            Code
                            ListeChampValeur {
                                ChampValeurDTO {
                                    Id
                                    Valeur
                                }
                            }
                        }
                    }
                    GetListeChampsGenerique(contexte: $contexte, critere: $criteres3) {
                        ChampDTO {
                            ChampTypeDonnee {
                                Code
                                Id
                                Libelle
                            }
                            ListChampListeValeur{
                                ChampListeValeurDTO {
                                    Code
                                    Id
                                    IsCloture
                                    OrdreAffichage
                                    Valeur
                                }
                            }
                            Id
                            Code
                            Libelle
                        }
                    }
                    GetListeSiteGenerique(contexte: $contexte, criteres: {}){
                        SiteDTO {
                            Id
                            Nom
                            IsCloture
                        }
                    }
                    GetListeOrganisationGenerique(contexte: $contexte, criteres: {}) {
                        OrganisationDTO {
                            Id
                            Nom
                            IsCloture
                        }
                    }
                }
            `,
            variables: {
                criteres2: {
                    ListeCodeCHP: 'CTRAT,GROUP,METIER,CATEG,SEX,CYCLE',
                    TakeChamp: true
                } as CriteresChampListeValeurDtoInput,
                criteres3: {
                    IsChampsPersoBeneficiaire: true,
                    TakeChampTypeDonnee: true,
                    TakeChampListeValeur: true
                } as CriteresChampDtoInput
            }
        }).pipe(
            tap(({
                     GetChampListeValGenerique,
                     GetListeChampsGenerique,
                     GetListeSiteGenerique,
                     GetListeOrganisationGenerique
                 }) => {
                // store values
                this.fieldsList = GetChampListeValGenerique?.ChampListeValeurDTO;
                this.fieldsList.sort((a, b) => a.OrdreAffichage - b.OrdreAffichage);

                // champs personnalisés
                this.personnalFields = (GetListeChampsGenerique?.ChampDTO || []).map(c => {
                    if (c.ChampTypeDonnee.Code === this.fieldsTypesCodes.LST) {
                        c.ListChampListeValeur.ChampListeValeurDTO.forEach(v => {
                            v.Champ = {Id: c.Id};
                        });
                    }
                    // on va créer un élément dans le formArray pour chaque champ personnalisé
                    this.champsPersonnalisesForm.push(new FormControl());
                    return c;
                });

                this._sitesGeneriques = GetListeSiteGenerique?.SiteDTO || [];
                this.filteredSites = this._sitesGeneriques.filter(s => this.AfficheClotures || !s.IsCloture);
                this._organisationsGeneriques = GetListeOrganisationGenerique?.OrganisationDTO || [];
                this.filteredOrganisations = this._organisationsGeneriques.filter(o => this.AfficheClotures || !o.IsCloture);
                this.cdr.markForCheck();
            })
        );
    }

    ngOnInit(): void {
        // on doit créer le formulaire avant
        this.createFiltersForm();
        this.watch$ = this.initList$();
    }

}
